import { AppShell } from '@mantine/core';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import i18next, { changeLanguage } from 'i18next';
import { useAppContext } from 'context/hooks';
import dayjs from 'dayjs';
import { NAVBAR_COLLAPSED_SIZE, NAVBAR_EXPANDED_SIZE } from 'styles';
import { LanguageCode, setDutchTerminology, setHighChartsOptions } from 'utils/i18n';
import { useShowRestricted } from 'hooks/useShowRestricted';
import { isProduction, showUsaFeature } from 'utils/app';
import { useHotkeys } from '@mantine/hooks';
import { Header } from './Header/Header';
import { NavBar } from './NavBar/NavBar';
import classes from './NavBar/NavBar.module.css';

export const Shell = () => {
  const { currentUser, isNavBarCollapsed } = useAppContext();
  const { showRestricted } = useShowRestricted();

  const setLanguage = (language?: string, country?: string) => {
    if (!language) return;
    const locale = showUsaFeature ? LanguageCode.English.toLocaleLowerCase() : language.toLowerCase();
    dayjs.locale(locale);
    setHighChartsOptions(locale);
    if (language === LanguageCode.Dutch) {
      setDutchTerminology(country);
    }
    if (i18next.language !== language) {
      void changeLanguage(language);
    }
  };

  const handleHotKay = (language?: string, country?: string) => !isProduction && setLanguage(language, country);

  useHotkeys([['alt+K', () => handleHotKay('cimode')]]);
  useHotkeys([['alt+E', () => handleHotKay(showUsaFeature ? LanguageCode.American_English : LanguageCode.English)]]);
  useHotkeys([['alt+F', () => handleHotKay(LanguageCode.French)]]);
  useHotkeys([['alt+G', () => handleHotKay(LanguageCode.German)]]);
  useHotkeys([['alt+S', () => handleHotKay(LanguageCode.Spanish)]]);
  useHotkeys([['alt+I', () => handleHotKay(LanguageCode.Italian)]]);
  useHotkeys([['alt+D', () => handleHotKay(LanguageCode.Dutch)]]);
  useHotkeys([
    [
      'alt+X',
      () =>
        handleHotKay(
          showUsaFeature ? LanguageCode.American_English : currentUser?.user.language,
          currentUser?.profile.country,
        ),
    ],
  ]);

  useEffect(() => {
    setLanguage(
      showUsaFeature ? LanguageCode.American_English : currentUser?.user.language,
      currentUser?.profile.country,
    );
  }, [currentUser]);

  if (showRestricted) {
    return (
      <AppShell layout={'alt'} padding={'xl'} bg={'var(--mantine-color-shell-5)'}>
        <AppShell.Main>
          <Outlet />
        </AppShell.Main>
      </AppShell>
    );
  }

  return (
    <AppShell
      layout={'alt'}
      navbar={{
        width: isNavBarCollapsed ? NAVBAR_COLLAPSED_SIZE : NAVBAR_EXPANDED_SIZE,
        breakpoint: 0,
      }}
      padding={'xl'}
      bg={'var(--mantine-color-shell-5)'}
    >
      <AppShell.Navbar p={'md'} className={classes.navbar}>
        <NavBar />
      </AppShell.Navbar>
      <AppShell.Main>
        <Header />
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
};
